import React, { useState, useRef, useContext } from 'react'
import { type Cat } from '@/type/cat'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'
import {
  Pencil,
  Plus,
  Share2,
  Maximize2,
  Minimize2,
  X,
  Camera,
  Image,
  Loader2,
} from 'lucide-react'
import EditCatProfile from '@/components/modals/EditCatProfile'
import { usePostsByCatId } from '@/services/posts/hooks'
import { Post } from '@/type/post'
import { cn } from '@/lib/styled'
import NewPost from './NewPost'
import { useCatById } from '@/services/cats/hook'
import { GlobalContext, GlobalContextType } from '@/store/GlobalContext'
const CatDetails = (props: {
  show: [boolean, (state: boolean) => void]
  selectedCat: Cat
  onClose: () => void
}) => {
  const [show, setShow] = props.show
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [showPhotos, setShowPhotos] = useState<boolean>(false)
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false)
  const [showNewPost, setShowNewPost] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { selectedCat, onClose } = props

  const { mutate: mutateAllCats } = useContext(GlobalContext) as GlobalContextType

  const {
    data: posts,
    isLoading: isLoadingPosts,
    mutate: mutatePosts,
  } = usePostsByCatId(selectedCat?.id)

  const {
    data: cat,
    isLoading: isLoadingCat,
    mutate: mutateCat,
  } = useCatById(selectedCat?.id)

  const allImages = posts?.map((post: Post) => post.images).flat()

  const handleAddPhoto = async () => {
    try {
      // Check if device has camera
      const devices = await navigator.mediaDevices.enumerateDevices()
      const hasCamera = devices.some((device) => device.kind === 'videoinput')

      if (hasCamera) {
        // Open camera
        // const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        // Here you would typically show a camera preview and capture UI
        // For now, we'll just open the file picker
        fileInputRef.current?.click()
      } else {
        // If no camera, just open file picker
        fileInputRef.current?.click()
      }
    } catch (err) {
      console.error('Error accessing camera:', err)
      // Fallback to file picker
      fileInputRef.current?.click()
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Here you would typically upload the file to your server
      console.log('Selected file:', file)
    }
  }

  const handleShare = async () => {
    try {
      await navigator.share({
        title: cat.name,
        text: `Check out ${cat.name} at ${cat.address}`,
        url: window.location.href,
      })
    } catch (err) {
      console.error('Error sharing:', err)
    }
  }

  const toggleFullscreen = () => {
    setIsExpanded(!isExpanded)
  }

  if(isLoadingCat){
    return (
      <div className='flex items-center justify-center h-full'>
        <Loader2 className='h-4 w-4 animate-spin' />
      </div>
    )
  }

  return (
    <Drawer
      open={show}
      onOpenChange={(open) => {
        setShow(open)
        if (!open) {
          onClose()
          setIsExpanded(false)
        }
      }}
    >
      <DrawerContent
        className={`mt-auto transition-all duration-300 ${
          isExpanded ? 'h-[100vh]' : 'h-[65vh]'
        } overflow-hidden`}
      >
        <div className='mx-auto w-full max-w-2xl flex flex-col h-full overflow-y-auto'>
          <div className='mx-auto mt-4 h-1 w-8 rounded-full bg-gray-600' />
          <DrawerHeader className='flex-none'>
            {/* starts:: Header */}
            <div className='flex items-start justify-between'>
              <div className='flex flex-col gap-2'>
                <img
                  src={cat?.images[0]}
                  alt='cat'
                  className='h-30 w-30 rounded-md mb-2 object-cover'
                />
                <DrawerTitle className='text-2xl text-start'>
                  {cat?.name}
                </DrawerTitle>
                <DrawerDescription className='text-sm text-gray-400 text-start'>
                  {cat?.address}
                </DrawerDescription>
                <p className='text-sm text-gray-600 text-start'>{cat?.bio}</p>
              </div>
              <div className='flex items-center gap-3'>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200'
                  onClick={handleShare}
                >
                  <Share2 className='h-4 w-4 text-gray-600' />
                </Button>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200'
                  onClick={toggleFullscreen}
                >
                  {isExpanded ? (
                    <Minimize2 className='h-4 w-4 text-gray-600' />
                  ) : (
                    <Maximize2 className='h-4 w-4 text-gray-600' />
                  )}
                </Button>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200'
                  onClick={() => {
                    setShow(false)
                    onClose()
                  }}
                >
                  <X className='h-4 w-4 text-gray-600' />
                </Button>
              </div>
            </div>
            <div className='grid grid-cols-3 gap-2 mt-2'>
              <Button
                variant='primary'
                size='lg'
                className='col-span-2'
                onClick={() => setShowNewPost(true)}
              >
                <Plus className='w-4 h-4' />
                <span>Write a post</span>
              </Button>
              <Button
                variant='outline'
                size='lg'
                className='col-span-1 border-gray-300'
                onClick={() => setShowEditProfile(true)}
              >
                <Pencil className='w-4 h-4' />
                <span>Edit</span>
              </Button>
            </div>
            {/* ends:: Header */}

            {/* starts:: img preview */}
            <div className='w-full flex gap-2 overflow-x-auto my-3'>
              {allImages?.map((image: string, index: number) => {
                return (
                  <img
                    key={index}
                    src={image}
                    alt='cat'
                    className='w-40 h-full object-cover rounded-md'
                  />
                )
              })}
              <div
                className={cn(
                  'flex gap-2 text-sm text-[#EF771E]',
                  allImages?.length > 0 ? 'flex-col' : 'flex-1'
                )}
              >
                <button
                  className={cn(
                    'flex-1 rounded-lg bg-gray-100 p-3 flex flex-col items-center justify-center gap-2',
                    allImages?.length > 0 ? 'w-25' : 'w-full'
                  )}
                  onClick={handleAddPhoto}
                >
                  <Camera className='h-6 w-6' />
                  <span className='text-xs'>Add photo</span>
                </button>
                <button
                  className={cn(
                    'flex-1 rounded-lg bg-gray-100 p-3 flex flex-col items-center justify-center gap-2',
                    allImages?.length > 0 ? 'w-25' : 'hidden'
                  )}
                  onClick={() => setShowPhotos(true)}
                >
                  <Image className='h-6 w-6' />
                  <span className='text-xs'>More photos</span>
                </button>
              </div>
            </div>
            {/* ends:: img preview */}

            {/* starts:: posts */}
            <div className='flex-1 overflow-y-auto border-t border-gray-200'>
              <h1 className='pt-5 font-medium text-start text-sm'>
                Latest updates
              </h1>
              <div className=''>
                {isLoadingPosts ? (
                  <div className='flex items-center justify-center h-full'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                  </div>
                ) : (
                  <>
                    {posts?.length > 0 ? (
                      posts?.map((post: Post, index: number) => {
                        return (
                          <div
                            key={index}
                            className='flex flex-col gap-2 border-b border-gray-200 py-4'
                          >
                            <p className='flex items-center gap-2'>
                              <span className='text-sm font-medium'>
                                Anonymous
                              </span>
                              <span className='text-sm text-gray-500'>
                                {index + 1}/4/2025 15:21
                              </span>
                            </p>
                            <p className='text-sm text-gray-500 text-start pb-1'>
                              {post.content}
                            </p>
                            {post.images.length === 1 ? ( // mapping images ref from Facebook
                              <img src={post.images[0]} alt='cat' className='w-full rounded-lg' />
                            ) : post.images.length === 2 ? (
                              <div className='grid grid-cols-2 gap-1'>
                                {post.images.map((image, index) => (
                                  <img key={index} src={image} alt='cat' className='w-full h-full object-cover rounded-lg' />
                                ))}
                              </div>
                            ) : post.images.length === 3 ? (
                              <div className='grid grid-cols-2 gap-1 h-80'>
                                <img src={post.images[0]} alt='cat' className='w-full h-full object-cover rounded-lg' />
                                <div className='grid grid-rows-2 gap-1'>
                                  <img src={post.images[1]} alt='cat' className='w-full h-full object-cover rounded-lg' />
                                  <img src={post.images[2]} alt='cat' className='w-full h-full object-cover rounded-lg' />
                                </div>
                              </div>
                            ) : (
                              <div className='grid grid-cols-2 gap-1'>
                                {post.images.slice(0, 3).map((image, index) => (
                                  <img key={index} src={image} alt='cat' className='w-full h-40 object-cover rounded-lg' />
                                ))}
                                <div className='relative'>
                                  <img src={post.images[3]} alt='cat' className='w-full h-40 object-cover brightness-50 rounded-lg' />
                                  {post.images.length > 4 && (
                                    <div className='absolute inset-0 flex items-center justify-center text-white text-2xl font-bold'>
                                      +{post.images.length - 4}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })
                    ) : (
                      <div className='flex items-center justify-center h-full'>
                        <p className='text-gray-500 py-5'>
                          No one posted about this cat yet
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* ends:: posts */}

            {/* Hidden file input */}
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept='image/*'
              className='hidden'
            />
          </DrawerHeader>
        </div>
      </DrawerContent>

      {/* Photos Modal */}
      <Dialog open={showPhotos} onOpenChange={() => setShowPhotos(false)}>
        <DialogContent className='max-w-4xl h-full p-0 overflow-auto bg-white'>
          <div className='flex flex-col h-full'>
            <div className='flex items-center justify-between p-4 border-b'>
              <DialogTitle>All Photos</DialogTitle>
            </div>
            <div className='grid grid-cols-3 gap-1 overflow-y-auto'>
              {allImages?.map((image: string, index: number) => {
                return (
                  <img
                    key={index}
                    src={image}
                    alt='cat'
                    className='overflow-hidden w-full h-35 object-cover aspect-video'
                  />
                )
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <EditCatProfile
        selectedCat={cat}
        show={[showEditProfile, setShowEditProfile]}
        onRequestSuccess={() => {
          setShowEditProfile(false)
          mutateCat()
          mutateAllCats()
        }}
      />

      {/* starts:: new post */}
      <NewPost
        selectedCat={cat}
        show={[showNewPost, setShowNewPost]}
        onRequestSuccess={() => {
          mutatePosts()
        }}
      />
      {/* ends:: new post */}
    </Drawer>
  )
}

export default CatDetails
